/**
 * User-related utilities
 */
import { User } from "~/api/API";

/**
 * Type guard for non-empty strings
 */
export const isNonEmptyString = (
  value: string | null | undefined,
): value is string => typeof value === "string" && value.trim().length > 0;

/**
 * Resolves a fallback email from a user's various email fields
 */
const resolveFallbackEmail = (user: User): string => {
  const assistantEmail = user.assistant?.email ?? "";
  const doctorContactEmail = user.doctor?.contact?.email ?? "";
  const doctorListEmail =
    user.doctor?.emails?.find((email): email is string =>
      isNonEmptyString(email),
    ) ?? "";
  return assistantEmail || doctorContactEmail || doctorListEmail || "";
};

/**
 * Resolves the display name for a user (for upload attribution, etc.)
 * Falls back to email or "Portal user" if no name is available.
 */
export const resolveUserDisplayName = (user: User | null): string => {
  if (!user) {
    return "Portal user";
  }

  const nameParts: string[] = [];
  if (isNonEmptyString(user.firstName)) {
    nameParts.push(user.firstName.trim());
  }
  if (isNonEmptyString(user.lastName)) {
    nameParts.push(user.lastName.trim());
  }

  if (nameParts.length > 0) {
    return nameParts.join(" ");
  }

  const fallbackEmail = resolveFallbackEmail(user);
  return fallbackEmail || "Portal user";
};
